// Uses sessionStorage for return URL (not localStorage) to isolate the return
// destination per-tab. sessionStorage is copied per-tab when a new same-origin
// tab/window is opened via window.open, avoiding cross-tab overwrites.
//
// NOTE: Concurrent impersonation tabs are NOT supported. The account/user data
// still uses localStorage (cross-tab shared), so changing impersonation in one
// tab makes all impersonation tabs reload with the newest account/user data.
// See Frontend/packages/customer-experience/src/components/ImpersonationBridge.tsx for additional background.
export const IMPERSONATION_RETURN_URL_KEY = "impersonation:return-url";

export const setImpersonationReturnUrl = (url: string) => {
  sessionStorage.setItem(IMPERSONATION_RETURN_URL_KEY, url);
};

export const getImpersonationReturnUrl = () => {
  return sessionStorage.getItem(IMPERSONATION_RETURN_URL_KEY);
};

export const clearImpersonationReturnUrl = () => {
  sessionStorage.removeItem(IMPERSONATION_RETURN_URL_KEY);
};

/**
 * Impersonation is keyed on the user's id or their email.
 */
export type ImpersonationIdentity = {
  userId?: string | null;
  email?: string | null;
};

export const impersonateUser = (
  identity: ImpersonationIdentity,
  demoData?: boolean,
  url = window.location.origin,
  openInNewWindow = false,
  newWindowName = "_blank",
) => {
  clearImpersonate(url, false);
  localStorage.setItem(
    "impersonateUser",
    JSON.stringify({
      userId: identity.userId ?? undefined,
      email: identity.email ?? undefined,
    }),
  );
  if (demoData) localStorage.setItem("demoData", "true");
  if (openInNewWindow) {
    window.open(url, newWindowName);
    return;
  }
  window.location.href = url;
};

export const clearImpersonate = (
  url = window.location.origin,
  navigate = true,
) => {
  localStorage.removeItem("impersonateUser");
  localStorage.removeItem("impersonateAccount");
  localStorage.removeItem("demoData");
  if (navigate) {
    window.location.href = url;
  }
};

export const impersonateAccount = (
  accountId: number | string,
  demoData?: boolean,
  url = window.location.origin,
  navigate = true,
  openInNewWindow = false,
  newWindowName = "_blank",
) => {
  clearImpersonate(url, false);
  localStorage.setItem("impersonateAccount", accountId.toString());
  if (demoData) localStorage.setItem("demoData", "true");
  if (openInNewWindow) {
    window.open(url, newWindowName);
    return;
  }
  if (navigate) {
    window.location.href = url;
  }
};

export const isImpersonatingAccount = () => {
  return !!localStorage.getItem("impersonateAccount");
};

export const isImpersonatingUser = () => {
  return !!localStorage.getItem("impersonateUser") && !isAssumingUserIdentity();
};

export const isAssumingUserIdentity = () => {
  const impersonateUserString = localStorage.getItem("impersonateUser");
  if (impersonateUserString) {
    try {
      const parsed = JSON.parse(impersonateUserString);
      return parsed.assumeIdentity === true || parsed.assumeIdentity === "true";
    } catch {
      // If it's not JSON, it's just a string ID
      return false;
    }
  }

  return false;
};
